import { BottomSheet } from './BottomSheet.js';
import { ControlsComponent } from './ControlsComponent.js';
import { EventBus, EVENTS } from '../../services/EventBus.js';
import * as PersistenceService from '../../services/PersistenceService.js';
import { QUICK_ACTIONS, DEFAULT_FAB_SETTINGS, QUICK_ACTION_MAP, getEnabledQuickActionIds } from '../mobileQuickActions.js';



export class ToolsBottomSheet extends BottomSheet {
    constructor(id, triggerElement, appContext) {
        super(id, triggerElement, { title: 'Simulation Tools' });
        this.appContext = appContext;
        this.worldManager = appContext.worldManager;
        this.render();
        this.attachEventListeners();
        this.contentContainer = this.sheetContent.querySelector('#mobileControlsMount');
        this.contentComponentType = ControlsComponent;

        // Keep the quick-actions row in sync when the customize list changes.
        this._subscribeToEvent(EVENTS.COMMAND_UPDATE_FAB_UI, () => this._renderQuickActions());
    }

    render() {
        const content = document.createElement('div');
        content.className = 'tools-bottom-sheet-content';

        content.innerHTML = `
            <div id="quick-actions-row" class="quick-actions-row" aria-label="Quick actions"></div>
            <div class="bottom-sheet-tabs">
                <button class="tab-button active" data-tab="tools">Tools</button>
                <button class="tab-button" data-tab="color">Color</button>
                <button class="tab-button" data-tab="customize-fabs">Customize</button>
            </div>
            <div class="bottom-sheet-panes">
                <div id="tools-pane" class="pane active">
                    <div id="mobileControlsMount"></div>
                    <div class="tool-group">
                        <h5>Reset / Clear</h5>
                        <div class="reset-clear-buttons">
                            <button class="button" data-action="reset">Reset World</button>
                            <button class="button" data-action="clear">Clear World</button>
                            <button class="button" data-action="capture-start">Save Start</button>
                        </div>
                    </div>
                </div>
                <div id="color-pane" class="pane hidden"></div>
                <div id="customize-fabs-pane" class="pane hidden">
                    <div class="tool-group">
                        <h5>Quick Actions (Select up to 3)</h5>
                        <ul id="fab-action-list" class="fab-action-list"></ul>
                    </div>
                </div>
            </div>
        `;

        this.setContent(content);
        const mobileControlsMount = content.querySelector('#mobileControlsMount');
        if (mobileControlsMount) {
            mobileControlsMount.innerHTML = '';
        }
        this.controlsComponent = null;
        this._renderQuickActions();
        this._initCustomizeFabsPane();
    }

    /**
     * Render the pinned quick-actions row (mobile redesign M2) — the former left
     * customizable FAB stack. Actions fire their command without dismissing the
     * sheet, so the world reacts live behind the half-height sheet (P1).
     */
    _renderQuickActions() {
        const row = this.sheetContent.querySelector('#quick-actions-row');
        if (!row) return;
        const fabSettings = PersistenceService.loadUISetting('fabSettings', DEFAULT_FAB_SETTINGS);
        const ids = getEnabledQuickActionIds(fabSettings);
        row.innerHTML = '';
        if (ids.length === 0) {
            row.innerHTML = '<span class="quick-actions-empty">Pick up to 3 quick actions in Customize.</span>';
            return;
        }
        ids.forEach(id => {
            const action = QUICK_ACTION_MAP[id];
            const button = document.createElement('button');
            button.className = 'quick-action-button';
            button.type = 'button';
            button.title = action.label;
            button.setAttribute('aria-label', action.label);
            button.innerHTML = `<span class="icon">${action.icon}</span><span class="quick-action-label">${action.label}</span>`;
            button.addEventListener('click', () => EventBus.dispatch(action.command, action.payload));
            row.appendChild(button);
        });
    }

    show() {
        super.show();
        
        // Mount controls component if not already mounted
        if (!this.controlsComponent) {
            this.controlsComponent = new ControlsComponent(this.appContext);
            if (this.contentContainer) {
                this.contentContainer.appendChild(this.controlsComponent.getElement());
            }
        }
        
        // Mount ChromaLab component for color tab
        const colorPane = this.sheetContent.querySelector('#color-pane');
        if (colorPane && colorPane.children.length === 0) {
            const chromaLabComponent = this.appContext.uiManager.sharedComponents.chromaLab;
            if (chromaLabComponent) {
                colorPane.appendChild(chromaLabComponent.getElement());
            }
        }
    }

    _initCustomizeFabsPane() {
        const fabActionList = this.sheetContent.querySelector('#fab-action-list');
        const savedSettings = PersistenceService.loadUISetting('fabSettings', DEFAULT_FAB_SETTINGS);
        QUICK_ACTIONS.forEach(action => {
            const isChecked = savedSettings.enabled.includes(action.id);
            const li = document.createElement('li');
            li.innerHTML = `
                <span class="icon">${action.icon}</span>
                <span class="text">${action.label}</span>
                <input type="checkbox" id="fab-toggle-${action.id}" class="checkbox-input fab-action-toggle" data-action-id="${action.id}" ${isChecked ? 'checked' : ''}>
                <label for="fab-toggle-${action.id}" class="checkbox-label">${isChecked ? 'Enabled' : 'Disabled'}</label>
            `;
            fabActionList.appendChild(li);
        });

    }

    attachEventListeners() {
        this.sheetContent.addEventListener('click', (e) => {
            const action = e.target.dataset.action;
            if (action === 'reset') {
                EventBus.dispatch(EVENTS.COMMAND_RESET_WORLDS_WITH_CURRENT_RULESET, { scope: 'selected' });
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Reset Selected World' });
                this.hide();
            } else if (action === 'clear') {
                EventBus.dispatch(EVENTS.COMMAND_CLEAR_WORLDS, { scope: 'selected' });
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Cleared Selected World' });
                this.hide();
            } else if (action === 'capture-start') {
                // The WorldManager handler owns the toast (shared by every dispatch source).
                EventBus.dispatch(EVENTS.COMMAND_CAPTURE_STATE_TO_LIBRARY, { assignScope: 'selected' });
                this.hide();
            }
            
            if (e.target.matches('.tab-button')) {
                const tabId = e.target.dataset.tab;
                this.sheetContent.querySelectorAll('.tab-button').forEach(b => b.classList.remove('active'));
                this.sheetContent.querySelectorAll('.pane').forEach(p => p.classList.add('hidden'));
                e.target.classList.add('active');
                this.sheetContent.querySelector(`#${tabId}-pane`).classList.remove('hidden');
            }
        });

        

        
        const fabPane = this.sheetContent.querySelector('#customize-fabs-pane');
        fabPane.addEventListener('change', e => {
            if (e.target.matches('.fab-action-toggle')) {
                const label = e.target.nextElementSibling;
                label.textContent = e.target.checked ? 'Enabled' : 'Disabled';
                const toggles = fabPane.querySelectorAll('.fab-action-toggle:checked');
                if (toggles.length > 3) {
                    EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'You can only enable up to 3 custom actions.', type: 'info' });
                    e.target.checked = false;
                    label.textContent = 'Disabled';
                    return;
                }

                const enabledIds = Array.from(toggles).map(t => t.dataset.actionId);
                const currentSettings = PersistenceService.loadUISetting('fabSettings', { enabled: [], locked: true });
                currentSettings.enabled = enabledIds;
                PersistenceService.saveUISetting('fabSettings', currentSettings);
                EventBus.dispatch(EVENTS.COMMAND_UPDATE_FAB_UI);
            }
        });
    }
}